/**
 * Authentication store.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, ApiError } from '@/lib/api'

export type AccessStatus = 'none' | 'pending' | 'rejected' | 'deactivated'

export interface User {
  id: string
  email: string | null
  name: string
  role: 'owner' | 'admin' | 'manager' | 'viewer'
  telegramId: number
  avatarUrl: string | null
}

export interface Tenant {
  id: string
  name: string
  slug: string
  plan: string
}

interface AuthState {
  user: User | null
  tenant: Tenant | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  accessStatus: AccessStatus

  authenticate: (initDataRaw: string) => Promise<void>
  logout: () => void
  refreshUser: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      tenant: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      accessStatus: 'none' as AccessStatus,

      authenticate: async (initDataRaw: string) => {
        set({ isLoading: true, error: null, accessStatus: 'none' })

        try {
          const response = await api.post<{
            token: string
            user: {
              id: string
              email: string | null
              first_name: string
              last_name: string | null
              role: 'owner' | 'admin' | 'manager' | 'viewer'
              telegram_id: number | null
              photo_url: string | null
            }
            tenant: Tenant
          }>('/auth/telegram/miniapp', { init_data: initDataRaw })

          api.setToken(response.token)

          const user: User = {
            id: response.user.id,
            email: response.user.email,
            name: [response.user.first_name, response.user.last_name].filter(Boolean).join(' '),
            role: response.user.role,
            telegramId: response.user.telegram_id ?? 0,
            avatarUrl: response.user.photo_url,
          }

          set({
            user,
            tenant: response.tenant,
            token: response.token,
            isAuthenticated: true,
            isLoading: false,
            accessStatus: 'none',
          })
        } catch (error) {
          if (error instanceof ApiError && error.status === 403) {
            const detail = (error.data as { detail?: string })?.detail || ''

            let accessStatus: AccessStatus = 'none'
            if (detail.includes('pending') || detail.includes('created')) {
              accessStatus = 'pending'
            } else if (detail.includes('rejected')) {
              accessStatus = 'rejected'
            } else if (detail.includes('deactivated')) {
              accessStatus = 'deactivated'
            }

            set({
              error: detail,
              isLoading: false,
              isAuthenticated: false,
              accessStatus,
            })
            return
          }

          const message = error instanceof Error ? error.message : 'Authentication failed'
          set({
            error: message,
            isLoading: false,
            isAuthenticated: false,
            accessStatus: 'none',
          })
        }
      },

      logout: () => {
        api.setToken(null)
        set({
          user: null,
          tenant: null,
          token: null,
          isAuthenticated: false,
          accessStatus: 'none',
        })
      },

      refreshUser: async () => {
        const { token } = get()
        if (!token) return

        try {
          const user = await api.get<User>('/users/me')
          set({ user })
        } catch {
          get().logout()
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
      }),
      onRehydrateStorage: () => (state) => {
        if (state?.token) {
          api.setToken(state.token)
        }
      },
    }
  )
)
